import React from 'react';

const CookiePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last updated:</strong> September 10, 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and enabling certain functionality.
              </p>
              <p className="text-gray-700 mb-4">
                Cookies can be "session" cookies (which are deleted when you close your browser) or "persistent" cookies (which remain on your device for a set period of time).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Cookies and Local Storage</h2>
              <p className="text-gray-700 mb-4">
                TrendBeat uses minimal tracking technologies for essential functionality:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Authentication:</strong> To maintain your login session and security</li>
                <li><strong>Form Data:</strong> To save your signup progress and form preferences</li>
                <li><strong>User Preferences:</strong> To remember your settings and customizations</li>
                <li><strong>Payment Processing:</strong> To enable secure payment transactions via Stripe</li>
                <li><strong>Security:</strong> To protect against fraud and unauthorized access</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Note:</strong> We do not use cookies for advertising, cross-site tracking, or behavioral profiling. Our focus is on providing essential functionality and security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Types of Data Storage We Use</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Essential Cookies</h3>
              <p className="text-gray-700 mb-4">
                These are necessary for TrendBeat to function properly. They enable authentication, session management, and security features.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>Examples:</strong> Authentication tokens, session identifiers, security cookies
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Local Storage</h3>
              <p className="text-gray-700 mb-4">
                We use browser local storage to save your form data, preferences, and signup progress. This data is stored locally on your device and is not transmitted to our servers unless you submit a form.
              </p>
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <p className="text-green-800 text-sm">
                  <strong>Examples:</strong> Signup form data, user preferences, theme settings, saved progress
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Third-Party Cookies (Stripe Only)</h3>
              <p className="text-gray-700 mb-4">
                We use Stripe for secure payment processing. Stripe may set cookies to ensure payment security and fraud prevention.
              </p>
              <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Examples:</strong> Stripe payment session cookies, fraud prevention tokens
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 What We Don't Use</h3>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> We do not use:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Advertising or marketing cookies</li>
                <li>Cross-site tracking cookies</li>
                <li>Social media tracking pixels</li>
                <li>Google Analytics or similar analytics tools</li>
                <li>Behavioral profiling or retargeting</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                We use the following third-party services that may set cookies or similar technologies:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Stripe:</strong> For secure payment processing and fraud prevention</li>
                <li><strong>Supabase:</strong> For authentication, database services, and user management</li>
                <li><strong>Vercel:</strong> For hosting and content delivery (may use performance cookies)</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Note:</strong> We do not use Google Analytics, Facebook Pixel, or other advertising/tracking services. Our third-party integrations are limited to essential functionality only.
              </p>
              <p className="text-gray-700 mb-4">
                These third parties have their own privacy policies and cookie practices. We recommend reviewing their policies for more information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Managing Your Cookie Preferences</h2>
              <p className="text-gray-700 mb-4">
                You have several options for managing cookies:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Browser Settings</h3>
              <p className="text-gray-700 mb-4">
                Most web browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Block all cookies</li>
                <li>Block third-party cookies only</li>
                <li>Delete existing cookies</li>
                <li>Set preferences for specific websites</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Local Storage Management</h3>
              <p className="text-gray-700 mb-4">
                You can clear local storage data through your browser's developer tools or settings. This will remove saved form data and preferences.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Impact of Disabling Storage</h3>
              <p className="text-gray-700 mb-4">
                Please note that disabling cookies and local storage may impact your experience:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>You may need to log in repeatedly</li>
                <li>Form data will not be saved automatically</li>
                <li>Some features may not work properly</li>
                <li>Payment processing may be affected</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                Different types of data have different retention periods:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Authentication tokens:</strong> Usually expire after 30 days of inactivity</li>
                <li><strong>Local storage:</strong> Remains until manually cleared or browser data is cleared</li>
                <li><strong>Server data:</strong> Retained according to our Privacy Policy</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> privacy@trendbeat.twokai.com<br />
                  <strong>Address:</strong> TwoKai Limited, Mansion House, Manchester Road, Altrincham, Cheshire, England, WA14 4RW, United Kingdom
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;